import { useCallback, useEffect, useRef } from "react";

export type UseTimeoutFnReturn = [() => boolean | null, () => void, () => void];

/**
 * A hook that executes a function after a specified delay and provides controls
 * to check status, cancel, or reset the timeout.
 *
 * @param fn - The function to execute after the timeout
 * @param ms - The delay in milliseconds before executing the function, defaults to 0
 * @returns A tuple containing:
 *          - isReady: Function that returns the current state
 *                     (true if executed, false if pending, null if cancelled)
 *          - clear: Function to cancel the pending timeout
 *          - set: Function to reset and start the timeout again
 *
 * @example
 * ```tsx
 * const [isReady, clear, reset] = useTimeoutFunction(() => {
 *   console.log('Executed after delay');
 * }, 1000);
 * ```
 */
export function useTimeoutFunction(fn: () => void, ms = 0): UseTimeoutFnReturn {
  const ready = useRef<boolean | null>(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const callback = useRef(fn);

  const isReady = useCallback(() => ready.current, []);

  const set = useCallback(() => {
    ready.current = false;
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => {
      ready.current = true;
      callback.current();
    }, ms);
  }, [ms]);

  const clear = useCallback(() => {
    ready.current = null;
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  // update ref when function changes
  useEffect(() => {
    callback.current = fn;
  }, [fn]);

  // set on mount, clear on unmount
  useEffect(() => {
    set();

    return clear;
  }, [ms]);

  return [isReady, clear, set];
}
